import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { doc, setDoc, onSnapshot, serverTimestamp, deleteField } from 'firebase/firestore';
import { loadFromStorage, saveToStorage } from '@/lib/storage';
import { LOCAL_STORAGE_KEYS } from '@/config/constants';
import { useRouteStatusSync } from '@/hooks/useRouteStatusSync';
import { updateRouteStatus } from '@/lib/routeStatusSync';
import { subscribeToProfiles, updateProfiles, DEFAULT_PROFILES } from '@/lib/profilesSync';
import { subscribeToWorkspaceMeta } from '@/lib/workspaceMetaSync';
import { subscribeToSecurity, updateAdminSecurity } from '@/lib/securitySync';
import { auth, db } from '@/firebase';


const ConfigContext = createContext();

export const useConfig = () => useContext(ConfigContext);

const defaultStatusLabels = {
  go: 'Go',
  salimos: 'Salimos',
  volvemos: 'Volvemos',
  fin: 'Fin',
};

const defaultRouteStatesConfig = {
  mode: '4',
  labelsFor2States: {
    state1: 'Go',
    state2: 'Fin',
  },
  labelsFor3States: {
    state1: 'Go',
    state2: 'Salimos',
    state3: 'Volvemos',
  },
  colors: {
    go: 'green',
    salimos: 'blue',
    volvemos: 'yellow',
    fin: 'red',
  },
};

/**
 * Suscripción individual (worker) a workspaces/{workspaceId}/workerProfiles/{profileId}
 * para cada profileId de assignedProfileIds. NUNCA lee settings/profiles ni
 * consulta la colección workerProfiles completa (sin list, solo onSnapshot
 * por documento individual — equivalente a get + updates en tiempo real).
 *
 * - Una suscripción onSnapshot por profileId.
 * - Solo agrega al array los documentos asignados que realmente existen.
 * - Mantiene orden determinista según el orden de assignedProfileIds.
 * - Devuelve una función que cancela TODAS las suscripciones.
 * - Ignora respuestas de suscripciones ya canceladas (guard `cancelled`).
 * - Si falta un documento o falla su lectura: se excluye ese perfil del
 *   array resultante. Nunca se recurre a settings/profiles como fallback.
 */
function subscribeToWorkerProfiles(workspaceId, assignedProfileIds, dbInstance, onProfilesChange) {
  if (!workspaceId || !dbInstance || !Array.isArray(assignedProfileIds) || assignedProfileIds.length === 0) {
    return () => {};
  }

  const safeOnProfilesChange = typeof onProfilesChange === 'function'
    ? onProfilesChange
    : () => {};

  let cancelled = false;
  const profilesById = {};

  const emit = () => {
    if (cancelled) return;
    // Reconstruir en el orden exacto de assignedProfileIds, omitiendo
    // los que no existen o no se han confirmado todavía.
    const ordered = assignedProfileIds
      .map((id) => profilesById[id])
      .filter(Boolean);
    safeOnProfilesChange(ordered);
  };

  const unsubscribes = assignedProfileIds.map((profileId) => {
    const profileRef = doc(dbInstance, 'workspaces', workspaceId, 'workerProfiles', profileId);
    return onSnapshot(
      profileRef,
      (snap) => {
        if (cancelled) return;
        if (snap.exists()) {
           const data = snap.data();
           profilesById[profileId] = {
             id: profileId,
             name: data.name || '',
            whatsapp: data.whatsapp || '',
            webUrl: data.webUrl || '',
            imageUrl: data.imageUrl || '',
            requireConfirmationWhenMarking: data.requireConfirmationWhenMarking ?? true,
            status: data.status ?? 'active',
            // Campos de PIN: se preservan solo si existen (compartidos con settings/profiles)
            ...(data.pinHash ? { pinHash: data.pinHash } : {}),
            ...(data.pinSalt ? { pinSalt: data.pinSalt } : {}),
            ...(data.pinIterations ? { pinIterations: data.pinIterations } : {}),
            ...(data.pinVersion ? { pinVersion: data.pinVersion } : {}),
            ...(data.pinUpdatedAt ? { pinUpdatedAt: data.pinUpdatedAt } : {}),
          };
        } else {
          // Documento asignado inexistente: excluir, sin fallback.
          delete profilesById[profileId];
        }
        emit();
      },
      (error) => {
        if (cancelled) return;
        console.error('[ConfigContext] Error subscribing to workerProfile:', profileId, error);
        // Fallo de lectura de este documento concreto: excluirlo, sin fallback.
        delete profilesById[profileId];
        emit();
      }
    );
  });

  return () => {
    cancelled = true;
    unsubscribes.forEach((unsub) => unsub());
  };
}

export const ConfigProvider = ({ children, workspaceId, userRole, assignedProfileIds }) => {
   // Guard: evita sobrescribir localStorage con defaults si los perfiles no han sido confirmados desde Firestore
   const profilesConfirmedRef = React.useRef(false);

   // profilesReady/companyMetaReady: estados reactivos (a diferencia del ref anterior,
   // un cambio de ref no provoca render). isConfigReady = ambos confirmados con snapshot real.
   const [profilesReady, setProfilesReady] = useState(false);
   const [companyMetaReady, setCompanyMetaReady] = useState(false);

   // Seguridad admin: PIN del administrador (rueda amarilla)
   // securityReady solo pasa a true tras snapshot real (exista o no el doc) — NUNCA tras error.
   const [security, setSecurity] = useState({
     adminPinHash: null,
     adminPinSalt: null,
     adminPinIterations: null,
     adminPinVersion: null,
     adminPinUpdatedAt: null,
   });
   const [securityReady, setSecurityReady] = useState(false);

   const [config, setConfig] = useState(() => {
    const userId = auth.currentUser?.uid;
    const savedConfig = loadFromStorage(LOCAL_STORAGE_KEYS.APP_CONFIG, null, userId);
    
    // Normalizar config con valores por defecto
    const normalized = savedConfig || {};
    
    return {
      // Sin fallback a APP_NAME: si no hay valor local guardado, queda vacío.
      // Un valor vacío nunca debe consolidarse como "RUTAPP guardado".
      companyName: normalized.companyName || '',
      companyLogo: normalized.companyLogo || null,
      profiles: normalized.profiles || DEFAULT_PROFILES,
      colorCodeEnabled: normalized.colorCodeEnabled ?? true,
      shareWhatsAppAccess: normalized.shareWhatsAppAccess ?? true,
      shareFleetAlerts: normalized.shareFleetAlerts ?? true,
      statusLabels: {
        ...defaultStatusLabels,
        ...(normalized.statusLabels || {}),
      },
      routeStatesConfig: {
        ...defaultRouteStatesConfig,
        ...(normalized.routeStatesConfig || {}),
      },
    };
  });

  // Sincronizar Estado de Ruta desde Firestore.
  // Se pasa workspaceId también para trabajadores: workspaceId ya es el
  // adminUid resuelto (ver AppProviders.jsx), por lo que el trabajador lee
  // el mismo documento workspaces/{adminUid}/settings/routeStatus que el
  // administrador, en modo solo lectura (ver firestore.rules). Esto permite
  // que shareFleetAlerts llegue como valor real compartido, no como default
  // local.
  const { routeStatus, loading: routeStatusLoading } = useRouteStatusSync(
    workspaceId
  );


   // Sincronizar Profiles desde Firestore.
   // Admin (userRole !== 'worker'): conserva EXACTAMENTE la sincronización
   // actual mediante subscribeToProfiles(workspaceId) sobre settings/profiles.
   // Worker (userRole === 'worker'): jamás lee settings/profiles. Se
   // suscribe individualmente a cada workerProfiles/{profileId} de
   // assignedProfileIds mediante subscribeToWorkerProfiles.
   //
   // Protección de identidad: al cambiar workspaceId, userRole o
   // assignedProfileIds, este efecto se relanza por completo (dependencias),
   // lo que cancela las suscripciones anteriores (cleanup) ANTES de crear
   // las nuevas, y resetea profilesReady/profilesConfirmedRef para no
   // arrastrar perfiles del workspace/identidad anterior.
   useEffect(() => {
     // Reset reactivo al cambiar workspaceId/userRole/assignedProfileIds:
     // no arrastrar el "ready" ni los perfiles de una identidad anterior.
     setProfilesReady(false);
     profilesConfirmedRef.current = false;
     setConfig(prevConfig => ({
       ...prevConfig,
       profiles: [],
     }));

     if (!workspaceId) {
       console.log("[ConfigContext] No workspaceId, skipping profiles sync");
       return;
     }

     if (userRole === 'worker') {
       if (!Array.isArray(assignedProfileIds) || assignedProfileIds.length === 0) {
         console.log("[ConfigContext] Worker sin assignedProfileIds, skipping profiles sync");
         return;
       }

       const unsubscribe = subscribeToWorkerProfiles(workspaceId, assignedProfileIds, db, (profiles) => {
         console.log("[ConfigContext] Worker profiles updated from Firestore:", profiles);
         profilesConfirmedRef.current = true;
         setProfilesReady(true);
         setConfig(prevConfig => ({
           ...prevConfig,
           profiles,
         }));
       });

       return () => unsubscribe();
     }

     // Modo administrador: comportamiento actual sin cambios.
     const unsubscribe = subscribeToProfiles(workspaceId, db, (profiles) => {
       console.log("[ConfigContext] Profiles updated from Firestore:", profiles);
       profilesConfirmedRef.current = true;
       setProfilesReady(true); // solo tras snapshot válido de settings/profiles
       setConfig(prevConfig => ({
         ...prevConfig,
         profiles,
       }));
     });

     return () => unsubscribe();
   }, [workspaceId, userRole, assignedProfileIds]);

    // Sincronizar companyName desde workspaceMetadata/{workspaceId}
    // companyMetaReady solo pasa a true ante un snapshot real (exista o no companyName).
    // Un error de Firestore NO marca companyMetaReady=true (condición obligatoria del usuario).
    // Funciona tanto para administrador como para trabajador.
    useEffect(() => {
      setCompanyMetaReady(false); // reset reactivo al cambiar workspaceId

      if (!workspaceId) {
        return;
      }

      const unsubscribe = onSnapshot(
        doc(db, 'workspaceMetadata', workspaceId),
        (docSnap) => {
           if (docSnap.exists()) {
             const data = docSnap.data();
             if (data.companyName || data.showAllProfilesInfo !== undefined) {
                setConfig(prevConfig => ({
                  ...prevConfig,
                  ...(data.companyName && { companyName: data.companyName }),
                  ...(data.showAllProfilesInfo !== undefined && {
                    showAllProfilesInfo: data.showAllProfilesInfo,
                  }),
                  ...(data.allowCrossProfileClientsConsultation !== undefined && {
                    allowCrossProfileClientsConsultation: data.allowCrossProfileClientsConsultation,
                  }),
                }));
             }
           }
          // Si el doc no existe o no tiene companyName: snapshot real confirmado, pero sin valor.
          // No se toca config.companyName (permanece vacío o con el valor local previo).
          setCompanyMetaReady(true);
        },
        (error) => {
          // Error real: no se pudo confirmar la lectura. No marcar companyMetaReady=true.
          console.warn('[ConfigContext] Error syncing workspace metadata companyName:', error);
        }
      );

      return () => unsubscribe();
    }, [workspaceId]);

   // Sincronizar seguridad admin (PIN administrador) desde Firestore
   // securityReady solo pasa a true tras snapshot real (exista o no el doc) — NUNCA tras error,
   // para evitar que la rueda amarilla interprete "sin PIN" antes de confirmar el estado real.
   useEffect(() => {
     setSecurityReady(false); // reset reactivo al cambiar workspaceId

     if (!workspaceId) {
       return;
     }

     if (userRole === 'worker') {
       setSecurityReady(true);
       return;
     }

     const unsubscribe = subscribeToSecurity(workspaceId, db, (securityData, error) => {
       if (error) {
         // Error real: no se pudo confirmar la lectura. No marcar securityReady=true.
         console.warn("[ConfigContext] Error syncing security, securityReady stays false");
         return;
       }
       setSecurity(securityData);
       setSecurityReady(true);
     });

     return () => unsubscribe();
   }, [workspaceId, userRole]);


  // Cuando cambia routeStatus desde Firestore, actualizar la config

  useEffect(() => {
    if (routeStatus && !routeStatusLoading) {
      setConfig(prevConfig => ({
        ...prevConfig,
        colorCodeEnabled: routeStatus.enabled,
        shareWhatsAppAccess: routeStatus.shareWhatsAppAccess ?? true,
        shareFleetAlerts: routeStatus.shareFleetAlerts ?? true,
        statusLabels: {
          go: routeStatus.labels[0] || 'Go',
          salimos: routeStatus.labels[1] || 'Salimos',
          volvemos: routeStatus.labels[2] || 'Volvemos',
          fin: routeStatus.labels[3] || 'Fin',
        },
        routeStatesConfig: {
          ...prevConfig.routeStatesConfig,
          mode: String(routeStatus.count),
          labelsFor2States: {
            state1: routeStatus.labels[0] || 'Go',
            state2: routeStatus.labels[1] || 'Fin',
          },
          labelsFor3States: {
            state1: routeStatus.labels[0] || 'Go',
            state2: routeStatus.labels[1] || 'Salimos',
            state3: routeStatus.labels[2] || 'Volvemos',
          },
          colors: {
            go: routeStatus.colors?.go || 'green',
            salimos: routeStatus.colors?.salimos || 'blue',
            volvemos: routeStatus.colors?.volvemos || 'yellow',
            fin: routeStatus.colors?.fin || 'red',
          },
        },
      }));
    }
  }, [routeStatus, routeStatusLoading]);

  // Recargar config cuando cambia el usuario autenticado
  useEffect(() => {
    const userId = auth.currentUser?.uid;
    if (userId) {
      const savedConfig = loadFromStorage(LOCAL_STORAGE_KEYS.APP_CONFIG, null, userId);
      const normalized = savedConfig || {};
      
      setConfig({
        // Sin fallback a APP_NAME: mismo criterio que el estado inicial (ver arriba).
        companyName: normalized.companyName || '',
        companyLogo: normalized.companyLogo || null,
        profiles: normalized.profiles || DEFAULT_PROFILES,
        colorCodeEnabled: normalized.colorCodeEnabled ?? true,
        shareWhatsAppAccess: normalized.shareWhatsAppAccess ?? true,
        shareFleetAlerts: normalized.shareFleetAlerts ?? true,
        statusLabels: {
          ...defaultStatusLabels,
          ...(normalized.statusLabels || {}),
        },
        routeStatesConfig: {
          ...defaultRouteStatesConfig,
          ...(normalized.routeStatesConfig || {}),
        },
      });
    }
  }, [auth.currentUser?.uid]);


   useEffect(() => {
     const userId = auth.currentUser?.uid;
     // Guard: no guardar en localStorage hasta que los perfiles hayan sido confirmados desde Firestore
     // Esto previene sobrescribir con defaults cuando arranca offline/mala cobertura
     if (!profilesConfirmedRef.current) {
       console.log("[ConfigContext] Profiles not yet confirmed from Firestore, skipping localStorage save");
       return;
     }
     saveToStorage(LOCAL_STORAGE_KEYS.APP_CONFIG, config, userId);
   }, [config]);

  const updateConfig = (newConfig) => {
    setConfig(prevConfig => ({ ...prevConfig, ...newConfig }));
  };

  /**
   * Actualiza la configuración de Estado de Ruta en Firestore
   * Se usa desde SettingsModal
   */
   const updateRouteStatusConfig = async (updates) => {
     if (userRole === 'worker') {
       console.warn("[ConfigContext] Worker cannot perform this administrative operation");
       return false;
     }

     if (!workspaceId || !auth.currentUser) {
       console.warn("[ConfigContext] Cannot update route status: no workspaceId or user");
       return false;
     }

     try {
      const fsUpdates = {};
      
      if (updates.enabled !== undefined) {
        fsUpdates.enabled = updates.enabled;
      }
      
      if (updates.count !== undefined) {
        fsUpdates.count = updates.count;
      }
      
      if (updates.labels) {
        fsUpdates.labels = updates.labels;
      }
       if (updates.colors) {
         fsUpdates.colors = updates.colors;
       }


      if (updates.shareWhatsAppAccess !== undefined) {
        fsUpdates.shareWhatsAppAccess = updates.shareWhatsAppAccess;
      }

      if (updates.shareFleetAlerts !== undefined) {
        fsUpdates.shareFleetAlerts = updates.shareFleetAlerts;
      }

      const success = await updateRouteStatus(workspaceId, db, fsUpdates, auth.currentUser.uid);
      return success;
    } catch (error) {
      console.error("[ConfigContext] Error updating route status:", error);
      return false;
    }
  };

   /**
    * Actualiza los Profiles en Firestore (solo administrador)
    * Se usa desde SettingsModal
    * NOTA: esta función escribe en settings/profiles y proyecta a workerProfiles.
    * Es una función administrativa; en modo worker no debe invocarse.
    */
   const updateProfilesConfig = async (profilesList) => {
     if (userRole === 'worker') {
       console.warn("[ConfigContext] Worker cannot perform this administrative operation");
       return false;
     }

     if (!workspaceId || !auth.currentUser) {
       console.warn("[ConfigContext] Cannot update profiles: no workspaceId or user");
       return false;
     }

     try {
       const success = await updateProfiles(workspaceId, db, profilesList, auth.currentUser.uid);
       return success;
     } catch (error) {
       console.error("[ConfigContext] Error updating profiles:", error);
       return false;
     }
   };

   /**
    * Actualiza el perfil propio del trabajador en workerProfiles/{profileId}
    * Permite solo whatsapp y requireConfirmationWhenMarking
    * Nunca escribe en settings/profiles
    */
   const updateOwnWorkerProfile = async (profileId, updates) => {
     if (userRole !== 'worker') {
       console.warn("[ConfigContext] Only workers can use this function");
       return false;
     }

     if (!workspaceId || !auth.currentUser) {
       console.warn("[ConfigContext] Cannot update worker profile: no workspaceId or user");
       return false;
     }

     // Verificar que el profileId pertenece a assignedProfileIds
     if (!Array.isArray(assignedProfileIds) || !assignedProfileIds.includes(profileId)) {
       console.warn("[ConfigContext] Worker not assigned to this profile:", profileId);
       return false;
     }

     try {
       const workerProfileRef = doc(db, "workspaces", workspaceId, "workerProfiles", profileId);

       // Construir payload: solo los campos permitidos.
       // imageUrl NUNCA se acepta aquí (queda fuera de esta ejecución).
       const payload = {};

       if ('name' in updates) {
         const trimmedName = (updates.name || '').trim();
         if (!trimmedName) {
           console.warn("[ConfigContext] Rejected: name cannot be empty");
           return false;
         }
         payload.name = trimmedName;
       }
       if ('whatsapp' in updates) {
         payload.whatsapp = updates.whatsapp || '';
       }
       if ('webUrl' in updates) {
         payload.webUrl = updates.webUrl || '';
       }
       if ('requireConfirmationWhenMarking' in updates) {
         payload.requireConfirmationWhenMarking = updates.requireConfirmationWhenMarking ?? false;
       }

       // Contrato del PIN: todo-o-nada.
       // - Si no llega ninguna clave de PIN, no se altera el PIN existente.
       // - Si llegan las 5 claves con valores completos y tipos válidos, se escriben las 5.
       // - Si llegan las 5 claves explícitamente como null, se traducen a deleteField() (borrado real).
       // - Si llega un subconjunto o una mezcla de valores completos y null, se rechaza sin escribir.
       const pinKeys = ['pinHash', 'pinSalt', 'pinIterations', 'pinVersion', 'pinUpdatedAt'];
       const presentPinKeys = pinKeys.filter((key) => key in updates);

       if (presentPinKeys.length > 0) {
         if (presentPinKeys.length !== pinKeys.length) {
           console.warn("[ConfigContext] Rejected: partial PIN state (missing keys)", presentPinKeys);
           return false;
         }

         const allNull = pinKeys.every((key) => updates[key] === null);
         const allComplete = pinKeys.every((key) => updates[key] !== null && updates[key] !== undefined);

         if (allNull) {
           // Eliminación real de los 5 campos directos del documento
           payload.pinHash = deleteField();
           payload.pinSalt = deleteField();
           payload.pinIterations = deleteField();
           payload.pinVersion = deleteField();
           payload.pinUpdatedAt = deleteField();
         } else if (allComplete) {
           if (
             typeof updates.pinHash !== 'string' ||
             typeof updates.pinSalt !== 'string' ||
             typeof updates.pinIterations !== 'number' ||
             typeof updates.pinVersion !== 'number' ||
             typeof updates.pinUpdatedAt !== 'string'
           ) {
             console.warn("[ConfigContext] Rejected: invalid PIN field types");
             return false;
           }
           payload.pinHash = updates.pinHash;
           payload.pinSalt = updates.pinSalt;
           payload.pinIterations = updates.pinIterations;
           payload.pinVersion = updates.pinVersion;
           payload.pinUpdatedAt = updates.pinUpdatedAt;
         } else {
           // Mezcla de valores completos y null: estado parcial, rechazar
           console.warn("[ConfigContext] Rejected: mixed complete/null PIN state");
           return false;
         }
       }

       if (Object.keys(payload).length === 0) {
         console.warn("[ConfigContext] No valid fields to update");
         return false;
       }

       await setDoc(workerProfileRef, payload, { merge: true });
       console.log("[ConfigContext] ✅ Worker profile updated:", Object.keys(payload));
       return true;
     } catch (error) {
       console.error("[ConfigContext] Error updating worker profile:", error);
       return false;
     }
   };

   /**
    * Actualiza los metadatos del workspace en Firestore (para administración)
    * Se usa desde SettingsModal al guardar cambios
    */
    const updateWorkspaceMeta = async ({ companyName, profiles, showAllProfilesInfo, allowCrossProfileClientsConsultation }) => {
     if (userRole === 'worker') {
       console.warn("[ConfigContext] Worker cannot perform this administrative operation");
       return false;
     }

     if (!workspaceId || !auth.currentUser) {
       console.warn("[ConfigContext] Cannot update workspace meta: no workspaceId or user");
       return false;
     }

      try {
        const wsRef = doc(db, "workspaces", workspaceId);
        const metaPayload = {
          companyName,
          profileNames: profiles.map(p => p.name),
          showAllProfilesInfo,
          allowCrossProfileClientsConsultation,
          updatedAt: serverTimestamp(),
        };
        // Only owner (auth.currentUser.uid === workspaceId) can update ownerEmail
        if (auth.currentUser.uid === workspaceId) {
          metaPayload.ownerEmail = auth.currentUser.email;
        }
        await setDoc(wsRef, {
          _meta: metaPayload
        }, { merge: true });
      console.log("[ConfigContext] ✅ Workspace meta updated");
      return true;
    } catch (error) {
      console.warn("[ConfigContext] Error updating workspace meta:", error);
      return false;
    }
  };

  /**
   * Actualiza únicamente los campos de seguridad admin (PIN administrador) en Firestore.
   * Se usa desde SettingsModal para crear/cambiar el PIN de administrador.
   */
  const updateAdminSecurityConfig = async (updates) => {
    if (userRole === 'worker') {
      console.warn("[ConfigContext] Worker cannot perform this administrative operation");
      return false;
    }

    if (!workspaceId || !auth.currentUser) {
      console.warn("[ConfigContext] Cannot update admin security: no workspaceId or user");
      return false;
    }

    try {
      const success = await updateAdminSecurity(workspaceId, db, updates, auth.currentUser.uid);
      return success;
    } catch (error) {
      console.error("[ConfigContext] Error updating admin security:", error);
      return false;
    }
  };

  /**
   * Elimina el PIN de administrador: borra únicamente los 4 campos
   * adminPinHash/adminPinSalt/adminPinIterations/adminPinVersion del
   * documento workspaces/{workspaceId}/settings/security mediante deleteField().
   * No borra el documento completo ni afecta a otros campos que pueda contener.
   * Tras esto, adminPinHash queda ausente y AppHeader interpretará el acceso
   * a Ajustes generales como directo (mismo camino que "sin PIN configurado").
   */
  const removeAdminPinConfig = async () => {
    if (userRole === 'worker') {
      console.warn("[ConfigContext] Worker cannot perform this administrative operation");
      return false;
    }

    if (!workspaceId || !auth.currentUser) {
      console.warn("[ConfigContext] Cannot remove admin PIN: no workspaceId or user");
      return false;
    }

    try {
      const success = await updateAdminSecurity(workspaceId, db, {
        adminPinHash: deleteField(),
        adminPinSalt: deleteField(),
        adminPinIterations: deleteField(),
        adminPinVersion: deleteField(),
      }, auth.currentUser.uid);
      return success;
    } catch (error) {
      console.error("[ConfigContext] Error removing admin PIN:", error);
      return false;
    }
  };

  // isConfigReady: true solo cuando AMBAS fuentes (settings/profiles y _meta) han sido

  // confirmadas con un snapshot real de Firestore (exista o no dato en cada una).
  const isConfigReady = profilesReady && companyMetaReady;

  return (
    <ConfigContext.Provider value={{
      config,
      workspaceId,
      userRole,
      assignedProfileIds,
      updateConfig,
      updateRouteStatusConfig,
      updateProfilesConfig,
      updateOwnWorkerProfile,
      updateWorkspaceMeta,
      isConfigReady,
      adminPinHash: security.adminPinHash,
      adminPinSalt: security.adminPinSalt,
      adminPinIterations: security.adminPinIterations,
      adminPinVersion: security.adminPinVersion,
      adminPinUpdatedAt: security.adminPinUpdatedAt,
      securityReady,
      updateAdminSecurityConfig,
      removeAdminPinConfig,
    }}>
      {children}
    </ConfigContext.Provider>
  );
};